import { API_BASE_URL } from "@/config/api";
import { getJsonAuthHeaders } from "@/lib/apiHeaders";
import { storage } from "@/services/storage";
import type { WeeklyReport } from "@/types/WeeklyReport";
import { v4 as uuidv4 } from "uuid";

// Helper function to get the current user's email
export const getCurrentUserEmail = () => {
  const userJson = storage.getItem("auth_user");
  if (userJson) {
    const user = JSON.parse(userJson);
    return user.email;
  }
  return null;
};

// Use centralized headers with CSRF protection
const getAuthHeaders = getJsonAuthHeaders;

export const reportService = {
  async getReports(email: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/reports?email=${email}`, {
        headers: getAuthHeaders(),
        credentials: "include", // Required for cookie-based auth
      });
      if (!response.ok) {
        throw new Error("Failed to fetch reports");
      }
      const data = await response.json();
      storage.setItem("reports", JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Error fetching reports:", error);
      throw error;
    }
  },
  async createReport(report: WeeklyReport) {
    try {
      const response = await fetch(`${API_BASE_URL}/reports`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(report),
        credentials: "include", // Required for cookie-based auth
      });

      if (!response.ok) {
        throw new Error("Failed to create report");
      }

      const data = await response.json();
      const reports = JSON.parse(storage.getItem("reports") || "[]");
      reports.push(data);
      storage.setItem("reports", JSON.stringify(reports));
      return data;
    } catch (error) {
      console.error("Error creating report:", error);
      throw error;
    }
  },
  async updateReport(id: string, report: WeeklyReport) {
    try {
      // Use POST endpoint since server handles both create and update
      const response = await fetch(`${API_BASE_URL}/reports`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(report),
        credentials: "include", // Required for cookie-based auth
      });

      if (!response.ok) {
        throw new Error("Failed to update report");
      }

      const data = await response.json();
      const reports = JSON.parse(storage.getItem("reports") || "[]");
      const index = reports.findIndex((r: WeeklyReport) => r.id === id);
      if (index !== -1) {
        reports[index] = data;
        storage.setItem("reports", JSON.stringify(reports));
      }
      return data;
    } catch (error) {
      console.error("Error updating report:", error);
      throw error;
    }
  },
  async deleteReport(id: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/reports/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: "include", // Required for cookie-based auth
      });

      if (!response.ok) {
        throw new Error("Failed to delete report");
      }

      const reports = JSON.parse(storage.getItem("reports") || "[]");
      const filteredReports = reports.filter((r: WeeklyReport) => r.id !== id);
      storage.setItem("reports", JSON.stringify(filteredReports));
      return true;
    } catch (error) {
      console.error("Error deleting report:", error);
      throw error;
    }
  },
  async saveReport(report: WeeklyReport) {
    // If report has an ID, update it; otherwise create a new one
    if (report.id) {
      return this.updateReport(report.id, report);
    } else {
      return this.createReport(report);
    }
  },

  // ============================================
  // Drafts 2.0 Methods
  // ============================================

  /**
   * Save a report as a draft
   * Sets status to 'draft' and updates timestamps
   */
  async saveDraft(report: WeeklyReport): Promise<WeeklyReport> {
    try {
      const userEmail = getCurrentUserEmail() || "unknown";
      const now = new Date().toISOString();

      const draftToSave = {
        ...report,
        status: "draft" as const,
        sentByEmail: false, // Ensure backward compatibility
        updatedAt: now,
        lastSavedBy: userEmail,
        createdAt: report.createdAt || now,
        draftMetadata: {
          ...report.draftMetadata,
          lastAutoSaveAt: now,
          hasUnsavedChanges: false,
          autoSaveIntervalMs: 30000, // 30 seconds
        },
      };

      // If draft already has an ID, update it; otherwise create new
      if (draftToSave.id) {
        return await this.updateReport(draftToSave.id, draftToSave);
      } else {
        return await this.createReport(draftToSave);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      throw error;
    }
  },

  /**
   * Autosave a draft (throttled saving)
   */
  async autosaveDraft(report: WeeklyReport): Promise<WeeklyReport> {
    try {
      const userEmail = getCurrentUserEmail() || "unknown";
      const now = new Date().toISOString();

      const draftToSave = {
        ...report,
        status: "draft" as const,
        sentByEmail: false,
        updatedAt: now,
        lastSavedBy: userEmail,
        draftMetadata: {
          ...report.draftMetadata,
          lastAutoSaveAt: now,
          hasUnsavedChanges: false,
        },
      };

      return await this.saveReport(draftToSave);
    } catch (error) {
      console.error("Error autosaving draft:", error);
      throw error;
    }
  },

  /**
   * Mark a draft as sent
   * Updates status to 'sent' and sets sentAt timestamp
   */
  async markAsSent(report: WeeklyReport): Promise<WeeklyReport> {
    try {
      const userEmail = getCurrentUserEmail() || "unknown";
      const now = new Date().toISOString();

      const sentReport = {
        ...report,
        status: "sent" as const,
        sentByEmail: true, // Ensure backward compatibility
        sentAt: now,
        updatedAt: now,
        lastSavedBy: userEmail,
        draftMetadata: undefined, // Clear draft metadata on send
      };

      return await this.updateReport(report.id!, sentReport);
    } catch (error) {
      console.error("Error marking report as sent:", error);
      throw error;
    }
  },

  /**
   * Get all draft reports for a user
   */
  async getDrafts(email: string): Promise<WeeklyReport[]> {
    try {
      const allReports = await this.getReports(email);
      return allReports.filter(
        (report) =>
          report.status === "draft" ||
          (report.status === undefined && report.sentByEmail === false)
      );
    } catch (error) {
      console.error("Error fetching drafts:", error);
      throw error;
    }
  },

  /**
   * Get all sent reports for a user
   */
  async getSentReports(email: string): Promise<WeeklyReport[]> {
    try {
      const allReports = await this.getReports(email);
      return allReports.filter(
        (report) =>
          report.status === "sent" ||
          (report.status === undefined && report.sentByEmail === true)
      );
    } catch (error) {
      console.error("Error fetching sent reports:", error);
      throw error;
    }
  },

  /**
   * Delete a draft
   */
  async deleteDraft(id: string): Promise<boolean> {
    return await this.deleteReport(id);
  },

  generateDemoReports() {
    const demoReports = [
      {
        id: "demo-report-1",
        date: new Date().toISOString(),
        dateRange: "This Week",
        to: ["demo@example.com"],
        cc: [],
        bcc: [],
        subject: "Demo Weekly Report",
        last_week_tasks: ["Complete project setup", "Review requirements"],
        accomplished: ["Project setup completed", "Requirements reviewed"],
        working_on: ["Feature development"],
        blockers: ["Waiting for design approval"],
        next_week_tasks: ["Implement core features", "Write tests"],
        notes: "Demo report for testing purposes",
        greeting: "Hi Team,\nPlease find my weekly report below.",
        signature: "BR,\nDemo User",
        createdBy: "demo@example.com",
        sentByEmail: false,
      },
    ];

    // Store demo reports in localStorage
    storage.setItem("reports", JSON.stringify(demoReports));
    console.log("Demo reports generated");
  },
};
